import React, { Component } from 'react';
import apiFile from 'src/helpers/apiFile';
import './Audio.slide.scss';
import { commonTimeout } from '../../../../../../../../helpers/commonTimeout';
import LessonContext from '../../../../LessonContext';
import { debounce, delay, isNil } from 'lodash';
import { SlideHeader } from 'src/pages/Course/Stages/Lessons/Cards/Components';
import { EventBus } from 'src/helpers/new';

export interface IProps {
    heading?: string;
    subHeading?: string;
    transcript?: string;
    content?: string;
    sourceAudio?: string;
    theme?: string;
    info?: string;
}

interface IState {
    fileUrl: string;
    isPlaying: boolean;
    volume: number;
    currentTime: number;
    duration: number;
    isMuted?: boolean;
    isShowIndicationTime: boolean;
}

export default class AudioSlide extends Component<IProps, IState> {
    static contextType = LessonContext;
    context!: React.ContextType<typeof LessonContext>;

    state: IState = {
        fileUrl: '',
        isPlaying: false,
        volume: 1,
        currentTime: 0,
        duration: 0,
        isShowIndicationTime: false,
    };

    track = null;
    audioContext = null;
    audioRef: React.RefObject<HTMLAudioElement> = React.createRef();

    componentDidMount() {
        this.loadAudioFile();
    }

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        if (this.props.sourceAudio !== prevProps.sourceAudio) {
            this.loadAudioFile();
        }
    }

    loadAudioFile = async () => {
        const { sourceAudio } = this.props;
        const { url } = await apiFile(sourceAudio);
        this.setState(
            {
                fileUrl: url,
            },
            () => {
                if (this.audioRef.current !== null) this.audioRef.current.volume = 1;
            },
        );
    };

    componentWillUnmount() {
        !this.context.isAdmin && this.clearInactiveTimer(false);
        EventBus.dispatch('resume-on-media-stop');
    }

    handlePlayButtonClick = () => {
        const { isPlaying } = this.state;

        this.setState(
            {
                isPlaying: !isPlaying,
            },
            () => {
                const audio = this.audioRef.current;
                if (audio) {
                    if (this.state.isPlaying) {
                        audio.play();
                        if (!this.context.isAdmin) {
                            EventBus.dispatch('resume-on-media-stop');
                            this.clearInactiveTimer(true);
                        }
                    } else {
                        audio.pause();
                        if (!this.context.isAdmin) {
                            EventBus.dispatch('pause-on-media-stop');
                            this.startInactiveTimer();
                        }
                    }
                }
            },
        );
    };

    startInactiveTimer = () => {
        this.context.clearMediaPlay(false);
        this.context.inactiveTimerCallback();
    };

    clearInactiveTimer = (state: boolean) => {
        commonTimeout.clearTimer('inactive');
        commonTimeout.clearTimer('logout');
        this.context.clearMediaPlay(state);
    };

    handleVolumeButtonClick = () => {
        const { isMuted } = this.state;

        if (!isMuted) {
            this.setState(
                {
                    isMuted: true,
                },
                () => {
                    if (this.audioRef.current !== null) this.audioRef.current.muted = true;
                },
            );
        } else {
            this.setState(
                {
                    isMuted: false,
                },
                () => {
                    if (this.audioRef.current !== null) this.audioRef.current.muted = false;
                },
            );
        }
    };

    handleMetaDataLoaded = () => {
        if (this.audioRef.current !== null) this.setState({ duration: this.audioRef.current.duration });
    };

    handleTimeUpdated = (e: any) => {
        const newTime = this.audioRef?.current?.currentTime;
        if (newTime !== this.state.currentTime && !isNil(newTime)) {
            this.setState({ currentTime: newTime });
        }
    };

    handlePlaybackEnded = (e: any) => {
        this.setState({
            isPlaying: false,
        });

        if (!this.context.isAdmin) {
            this.clearInactiveTimer(false);
            delay(() => {
                this.startInactiveTimer();
            }, 100);
        }
    };

    handleTrackClick = (e: any) => {
        requestAnimationFrame(() => {
            const rect = e.target.getBoundingClientRect(),
                { duration } = this.state,
                trackClickPositionPercentage = ((e.clientX - rect.left) / 210) * 100;

            const newTime = duration * ((trackClickPositionPercentage > 99 ? 100 : trackClickPositionPercentage) / 100);
            if (newTime !== this.state.currentTime) {
                this.setState({ currentTime: newTime });
                if (this.audioRef.current) this.audioRef.current.currentTime = newTime;
            }
        });
    };

    onHoverTrack = debounce((e: any) => {
        const rect = e.target.getBoundingClientRect(),
            { duration } = this.state,
            trackClickPositionPercentage = ((e.clientX - rect.left) / 210) * 100;

        const showHoverTime =
            duration * ((trackClickPositionPercentage > 99 ? 100 : trackClickPositionPercentage) / 100);

        if (showHoverTime <= 0) return;
        const progressTime = document.getElementById('tooltip-audio-indicator');
        const timelineWidth = document.getElementById('track')?.clientWidth;
        if (timelineWidth) {
            if (progressTime) {
                progressTime.style.left = `${Math.round(trackClickPositionPercentage)}%`;
                progressTime.innerText = this.secondsToTimeStamp(showHoverTime);
            }
            if (!this.state.isShowIndicationTime && Math.floor(showHoverTime) > 0) {
                this.setState({ isShowIndicationTime: true });
            }
        }
    }, 50);

    handleVolumeTrackClick = (e: any) => {
        const rect = e.target.getBoundingClientRect(),
            trackClickPositionPercentage = (e.clientX - rect.left) / 80;

        this.setState({ volume: trackClickPositionPercentage >= 0.95 ? 1 : trackClickPositionPercentage }, () => {
            const { volume } = this.state;
            if (this.audioRef.current !== null) this.audioRef.current.volume = volume;
        });
    };

    secondsToTimeStamp = (seconds: number) => {
        return isNaN(seconds) ? '' : new Date(seconds * 1000).toISOString().substr(14, 5);
    };

    get volumeLevelIcon() {
        const { isMuted, volume } = this.state;
        if (isMuted) {
            return 'fa-volume-mute';
        }

        if (volume === 0) {
            return 'fa-volume-off';
        }

        if (volume < 0.5) {
            return 'fa-volume-low';
        }

        return 'fa-volume-high';
    }

    handleMoveBackward = () => {
        this.setState(
            {
                currentTime: this.state.currentTime - 15,
            },
            () => {
                if (this.audioRef.current) {
                    this.audioRef.current.currentTime -= 15;
                }
            },
        );
    };

    handleMoveForward = () => {
        if (this.state.currentTime <= this.state.duration) {
            this.setState(
                {
                    currentTime: this.state.currentTime + 15,
                },
                () => {
                    if (this.audioRef.current) {
                        this.audioRef.current.currentTime += 15;
                    }
                },
            );
        }
    };

    onMouseDown = () => {
        const progressTime = document.getElementById('tooltip-audio-indicator');
        if (progressTime) {
            this.setState({ isShowIndicationTime: false });
            progressTime.style.left = `${0}%`;
            progressTime.innerText = '00:00';
        }
    };

    onMouseEnter = () => {
        this.setState({ isShowIndicationTime: true });
    };

    render() {
        const { isPlaying, isMuted, volume, currentTime, duration } = this.state,
            { heading, subHeading, transcript, theme, content } = this.props;

        return (
            <>
                <audio
                    controls
                    src={this.state.fileUrl}
                    ref={this.audioRef}
                    preload='metadata'
                    onDurationChange={this.handleMetaDataLoaded}
                    onTimeUpdate={this.handleTimeUpdated}
                    onEnded={this.handlePlaybackEnded}
                />
                <header>
                    <div className={`player ${theme === 'navy' ? 'white-controls' : ''} `}>
                        <div className='body'>
                            <div
                                className='track'
                                id='track'
                                onClick={this.handleTrackClick}
                                onMouseMove={this.onHoverTrack}
                                onMouseLeave={this.onMouseDown}
                                onMouseEnter={this.onMouseEnter}
                            >
                                {this.state.isShowIndicationTime && <span id='tooltip-audio-indicator'>00:00</span>}
                                <div
                                    className={`${theme === 'navy' ? 'white-controls' : ''}`}
                                    style={{ width: `${(currentTime * 100) / duration}%` }}
                                />
                            </div>

                            <div className='details'>
                                <div className='time'>
                                    {this.secondsToTimeStamp(currentTime)} / {this.secondsToTimeStamp(duration)}
                                </div>
                                <div className='volume'>
                                    <i
                                        className={`fa-solid ${this.volumeLevelIcon}`}
                                        onClick={this.handleVolumeButtonClick}
                                    ></i>
                                    <div onClick={this.handleVolumeTrackClick}>
                                        <div
                                            className={`${theme === 'navy' ? 'white-controls' : ''}`}
                                            style={{ width: `${isMuted ? 0 : volume * 100}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='audio-controls'>
                                <div className='audio-forword-arrow' onClick={this.handleMoveBackward}>
                                    <div className='audio-content'>15</div>
                                </div>
                                <i
                                    className={`play-pause fa-solid fa-${!isPlaying ? 'play' : 'pause'}`}
                                    onClick={this.handlePlayButtonClick}
                                ></i>

                                <div className='audio-backword-arrow' onClick={this.handleMoveForward}>
                                    <div className='audio-content'>15</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <SlideHeader heading={heading} subHeading={subHeading} specificClass={'audio_card'} />
                </header>
                {transcript && <div className='transcript' dangerouslySetInnerHTML={{ __html: transcript }} />}
                {content && !transcript && <div className='content' dangerouslySetInnerHTML={{ __html: content }} />}
            </>
        );
    }
}
